import { toast } from 'react-toastify';

import {
  CloseAndUnfollowResponse,
  closeAndUnfollow,
  removeLeader,
  removeLeaders,
} from 'api/copyBotApi';
import { useCopyBotAuth } from 'hooks';
import { store } from 'store';
import { Address } from 'viem';

/**
 * Unfollow one or more leaders.
 *
 * `closePositionsFirst` (default true) routes through the backend's safe
 * close-and-unfollow flow: every open position copied from the leader is
 * sold via CLOB / settled on-chain BEFORE the leader is removed from
 * wallet.leaders. If any close fails, the leader stays attached and the
 * user gets a toast describing which positions failed so they can retry.
 *
 * `closePositionsFirst=false` is the legacy hard-delete path. It calls
 * the backend `removeLeader` endpoint without `force`, which by default
 * refuses to remove a leader with open positions (409 Conflict). It is
 * effectively only useful for unfollowing a leader that has zero open
 * positions; otherwise the safe path is what the user wants.
 */
export const useUnfollow = () => {
  const { account } = store.web3Store;
  const { fetchPortfolio } = store.portfolioStore;
  const { getAccessToken, clearAccessToken } = useCopyBotAuth();

  const summarizeAttemptFailures = (response: CloseAndUnfollowResponse): string => {
    const failed = response.attempts.filter((attempt) => !attempt.closed);
    if (failed.length === 0) {
      return '';
    }
    // Show up to 2 titles + the count to keep the toast readable
    const sample = failed
      .slice(0, 2)
      .map((attempt) => attempt.title || attempt.conditionId.slice(0, 10) + '…')
      .join(', ');
    const tail = failed.length > 2 ? ` and ${failed.length - 2} more` : '';
    return `Could not close: ${sample}${tail}`;
  };

  const unfollow = async (leaders: Address[], closePositionsFirst = true) => {
    if (!account || !leaders.length) return false;

    try {
      const token = await getAccessToken(account);

      if (closePositionsFirst) {
        // Safe path. The backend has no batch close-and-unfollow endpoint,
        // so for multi-leader removals we sequence per-leader calls.
        // Sequential by design: parallel closes would race the relayer
        // quota and we want each leader's positions to either fully
        // close or surface a clean error before moving on.
        let totalClosed = 0;
        let totalFailed = 0;
        const failedLeaders: Address[] = [];

        for (const leader of leaders) {
          try {
            const response = await closeAndUnfollow(account, leader, token);
            totalClosed += response.totalClosed;
            totalFailed += response.totalFailed;

            if (!response.unfollowed) {
              failedLeaders.push(leader);
              const detail = summarizeAttemptFailures(response);
              toast.error(
                `Could not unfollow ${leader.slice(0, 6)}…${leader.slice(-4)}. ${detail}`.trim()
              );
            }
          } catch (err: any) {
            if (err?.response?.status === 401) {
              clearAccessToken(account);
              throw err;
            }
            console.error(`closeAndUnfollow failed for ${leader}:`, err);
            failedLeaders.push(leader);
            toast.error(
              `Failed to unfollow ${leader.slice(0, 6)}…${leader.slice(-4)}: ${
                err?.response?.data?.message || err?.message || 'unknown error'
              }`
            );
          }
        }

        await fetchPortfolio(true);

        const allUnfollowed = failedLeaders.length === 0;
        if (allUnfollowed) {
          if (totalClosed === 0 && totalFailed === 0) {
            toast.success(leaders.length > 1 ? 'Leaders removed' : 'Leader removed');
          } else {
            toast.success(
              `${leaders.length > 1 ? 'Leaders removed' : 'Leader removed'} ` +
                `(${totalClosed} position${totalClosed === 1 ? '' : 's'} closed)`
            );
          }
        }
        return allUnfollowed;
      }

      // Legacy / hard path — only works if leaders have zero open positions
      // (backend will 409 otherwise). Kept for callers that explicitly
      // want this behavior (e.g. cleanup tooling).
      if (leaders.length === 1) {
        await removeLeader(account, leaders[0], token);
      } else {
        await removeLeaders(account, { addresses: leaders }, token);
      }

      await fetchPortfolio(true);
      toast.success('Leader removed');
      return true;
    } catch (err: any) {
      if (err?.response?.status === 401) {
        clearAccessToken(account);
      }
      console.error('Failed to unfollow leader(s):', err);
      toast.error('Failed to unfollow');
      return false;
    }
  };

  return unfollow;
};
